import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Palette, Timer, Check, SkipForward, Undo2, Trash2, Eraser, Eye, Trophy, Play, Flag } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import useSyncedState from './useSyncedState';
import ReferenceScale from './ReferenceScale';

// Pictionary — one player draws a secret word, the others guess out loud.
//
// mode 'duo'   (student ↔ teacher): roles alternate every round. Only the
//               drawer sees the word, draws, and taps Guessed / Skip; the
//               point goes to the drawer.
// mode 'teams' (group play): teams take turns. The teacher is the host —
//               always sees the word and runs Guessed / Skip / Next. Anyone
//               may draw; on the student side the word is hidden behind a
//               press-and-hold "peek" button for the team's artist.
//
// Two synced channels so writers never race each other:
//   gameState    — phase, round, scores (written by the drawer/host)
//   strokesState — { roundKey, strokes: [{ id, c, w, p: [x,y,…] }] } (drawers)
// Stroke points live in a 1000-wide viewBox whose height follows the
// element's aspect ratio, so drawings look identical on every screen.

const VB_W = 1000;
const PUSH_MS = 300;
const COLORS = ['#1f2937', '#e11d48', '#2563eb', '#16a34a', '#f59e0b', '#7c3aed', '#8b5a2b'];
const SIZES = [5, 12, 26];

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const createInitialGame = () => ({ phase: 'idle' });
const createInitialStrokes = () => ({ roundKey: null, strokes: [] });

const pathFor = (p) => {
  if (!p || p.length < 2) return '';
  let d = `M${p[0]} ${p[1]}`;
  if (p.length === 2) return `${d} L${p[0] + 0.1} ${p[1]}`;
  for (let i = 2; i < p.length; i += 2) d += ` L${p[i]} ${p[i + 1]}`;
  return d;
};

const upsertStroke = (list, stroke) => {
  if (!stroke) return list;
  const idx = list.findIndex((s) => s.id === stroke.id);
  if (idx === -1) return [...list, stroke];
  const next = [...list];
  next[idx] = stroke;
  return next;
};

const useTimeLeft = (game, seconds) => {
  const [now, setNow] = useState(() => Date.now());
  const active = game.phase === 'drawing' && !!game.roundStartedAt;
  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [active, game.roundStartedAt]);
  if (!active) return seconds;
  return Math.min(seconds, Math.max(0, seconds - Math.floor((now - game.roundStartedAt) / 1000)));
};

const PictionaryInner = ({
  element,
  isEditor = false,
  previewMode = false,
  userRole = 'student',
  gameState = null,
  onGameStateChange,
  strokesState = null,
  onStrokesChange,
}) => {
  const { t } = useTranslation();
  const tg = (k, v) => t(`games.pictionary.${k}`, v);
  const {
    words: rawWords = [],
    mode = 'duo',
    teams = [],
    roundSeconds = 60,
    firstDrawer = 'student',
    canvasColor = '#ffffff',
    accentColor = '#2A4D25',
    fontFamily = 'Evolventa',
    fontSize = 16,
  } = element.content || {};
  const words = rawWords.map((w) => String(w || '').trim()).filter(Boolean);
  const seconds = Math.max(10, Number(roundSeconds) || 60);
  const teamList = teams.length >= 2 ? teams : [
    { id: 'team_a', name: 'Team A', color: '#2A4D25' },
    { id: 'team_b', name: 'Team B', color: '#e11d48' },
  ];
  const isTeams = mode === 'teams';

  const [game, updateGame] = useSyncedState(gameState, onGameStateChange, createInitialGame);
  const [strokeData, updateStrokes] = useSyncedState(strokesState, onStrokesChange, createInitialStrokes);
  const timeLeft = useTimeLeft(game, seconds);

  const roleName = (r) => (r === 'teacher' ? tg('teacher', { defaultValue: 'Вчитель' }) : tg('student', { defaultValue: 'Учень' }));
  const otherRole = (r) => (r === 'teacher' ? 'student' : 'teacher');
  const isHost = previewMode || userRole === 'teacher';
  const currentTeam = isTeams ? teamList[(game.teamIdx || 0) % teamList.length] : null;
  const isDrawer = previewMode || (isTeams ? true : game.drawer === userRole);
  // Who writes game transitions during a round: the drawer in duo, the host in teams.
  const controlsRound = isTeams ? isHost : (previewMode || game.drawer === userRole);
  const seesWord = previewMode || (isTeams ? isHost : game.drawer === userRole);
  const word = game.order ? game.order[game.index || 0] : null;
  const roundKey = game.phase === 'drawing' || game.phase === 'review' ? `${game.round}-${game.roundStartedAt || 0}` : null;

  // ---------- game transitions ----------
  const startGame = () => updateGame(() => ({
    phase: 'drawing',
    order: shuffle(words),
    index: 0,
    round: 1,
    drawer: firstDrawer === 'teacher' ? 'teacher' : 'student',
    teamIdx: 0,
    roundStartedAt: Date.now(),
    scores: {},
    history: [],
  }));

  const endRound = (result) => updateGame((prev) => {
    if (prev.phase !== 'drawing') return prev;
    const who = isTeams ? teamList[(prev.teamIdx || 0) % teamList.length].id : prev.drawer;
    const scores = { ...(prev.scores || {}) };
    if (result === 'guessed') scores[who] = (scores[who] || 0) + 1;
    return {
      ...prev,
      phase: 'review',
      scores,
      lastResult: result,
      history: [...(prev.history || []), { word: prev.order[prev.index || 0], who, result }],
    };
  });

  const nextRound = () => updateGame((prev) => {
    const index = (prev.index || 0) + 1;
    if (!prev.order || index >= prev.order.length) return { ...prev, phase: 'final' };
    return {
      ...prev,
      phase: 'drawing',
      index,
      round: (prev.round || 1) + 1,
      drawer: otherRole(prev.drawer),
      teamIdx: ((prev.teamIdx || 0) + 1) % teamList.length,
      roundStartedAt: Date.now(),
    };
  });

  const finishGame = () => updateGame((prev) => ({ ...prev, phase: 'final' }));

  // Timer expiry is dispatched by whoever controls the round.
  useEffect(() => {
    if (game.phase === 'drawing' && timeLeft <= 0 && controlsRound && !isEditor) endRound('timeout');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, game.phase, controlsRound]);

  // ---------- canvas ----------
  const boxRef = useRef(null);
  const [vbH, setVbH] = useState(560);
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const measure = () => {
      if (el.clientWidth && el.clientHeight) setVbH(Math.round((VB_W * el.clientHeight) / el.clientWidth));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [game.phase]);

  const [color, setColor] = useState(COLORS[0]);
  const [size, setSize] = useState(SIZES[0]);
  const [erasing, setErasing] = useState(false);
  const [liveStroke, setLiveStroke] = useState(null);
  const liveRef = useRef(null);
  const lastPushRef = useRef(0);
  const [peeking, setPeeking] = useState(false);

  const committed = strokeData.roundKey === roundKey ? (strokeData.strokes || []) : [];
  const shownStrokes = upsertStroke(committed, liveStroke);
  const canDraw = !isEditor && game.phase === 'drawing' && isDrawer && timeLeft > 0;

  const pushStroke = (stroke) => updateStrokes((prev) => {
    const base = prev.roundKey === roundKey ? (prev.strokes || []) : [];
    return { roundKey, strokes: upsertStroke(base, stroke) };
  });

  const pointFrom = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return [
      Math.round(((e.clientX - rect.left) / rect.width) * VB_W),
      Math.round(((e.clientY - rect.top) / rect.height) * vbH),
    ];
  };

  const onPointerDown = (e) => {
    if (!canDraw) return;
    e.preventDefault();
    e.stopPropagation();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    const stroke = {
      id: `s_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      c: erasing ? canvasColor : color,
      w: erasing ? SIZES[2] * 1.4 : size,
      p: pointFrom(e),
    };
    liveRef.current = stroke;
    setLiveStroke(stroke);
    lastPushRef.current = Date.now();
  };

  const onPointerMove = (e) => {
    const stroke = liveRef.current;
    if (!stroke) return;
    e.preventDefault();
    const [x, y] = pointFrom(e);
    const lx = stroke.p[stroke.p.length - 2];
    const ly = stroke.p[stroke.p.length - 1];
    if (Math.hypot(x - lx, y - ly) < 4) return;
    const next = { ...stroke, p: [...stroke.p, x, y] };
    liveRef.current = next;
    setLiveStroke(next);
    if (Date.now() - lastPushRef.current > PUSH_MS) {
      lastPushRef.current = Date.now();
      pushStroke(next);
    }
  };

  const onPointerUp = () => {
    const stroke = liveRef.current;
    if (!stroke) return;
    liveRef.current = null;
    pushStroke(stroke);
    setLiveStroke(null);
  };

  const undo = () => updateStrokes((prev) => (prev.roundKey === roundKey ? { ...prev, strokes: (prev.strokes || []).slice(0, -1) } : prev));
  const clear = () => updateStrokes(() => ({ roundKey, strokes: [] }));

  // ---------- render ----------
  const fs = fontSize;
  const primaryBtn = 'inline-flex items-center gap-1.5 rounded-full text-white font-semibold transition bg-[#2A4D25] hover:bg-[#1e3a1a]';
  const ghostBtn = 'inline-flex items-center gap-1.5 rounded-full font-medium transition bg-white border border-gray-200 hover:bg-gray-50 text-gray-700';
  const btnStyle = { fontSize: fs * 0.9, padding: '5px 14px' };

  if (isEditor) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-brand-primary/40 bg-brand-sage/10 p-4 text-brand-primary" style={{ fontFamily }}>
        <Palette size={40} className="mb-2 opacity-70" />
        <div className="text-lg font-semibold">Pictionary</div>
        <div className="text-xs opacity-70 mt-1 text-center">
          {isTeams
            ? tg('modeTeams', { count: teamList.length, defaultValue: `Команди (${teamList.length})` })
            : tg('modeDuo', { defaultValue: 'Учень ↔ вчитель' })}
          {' · '}
          {tg('wordsCount', { count: words.length, defaultValue: `${words.length} слів` })}
          {' · '}
          {seconds}s
        </div>
      </div>
    );
  }

  const scoreFor = (id) => (game.scores || {})[id] || 0;
  const scoreboard = isTeams
    ? teamList.map((tm) => ({ id: tm.id, name: tm.name, color: tm.color || accentColor, score: scoreFor(tm.id) }))
    : ['student', 'teacher'].map((r) => ({ id: r, name: roleName(r), color: r === 'student' ? accentColor : '#e11d48', score: scoreFor(r) }));

  const renderScores = (highlightId) => (
    <div className="flex items-center gap-1.5 flex-wrap">
      {scoreboard.map((s) => (
        <span
          key={s.id}
          className="inline-flex items-center gap-1.5 rounded-full font-semibold tabular-nums"
          style={{
            fontSize: fs * 0.8,
            padding: '2px 10px',
            backgroundColor: s.id === highlightId ? s.color : 'rgba(0,0,0,0.05)',
            color: s.id === highlightId ? '#ffffff' : '#374151',
          }}
        >
          {s.name}: {s.score}
        </span>
      ))}
    </div>
  );

  const shell = (children) => (
    <div className="w-full h-full flex flex-col overflow-hidden rounded-2xl bg-white" style={{ fontFamily }}>{children}</div>
  );

  if (words.length === 0) {
    return shell(
      <div className="flex-1 flex flex-col items-center justify-center text-gray-400 gap-2">
        <Palette size={36} />
        <div style={{ fontSize: fs * 0.9 }}>{tg('noWords', { defaultValue: 'Додайте слова в налаштуваннях' })}</div>
      </div>,
    );
  }

  if (game.phase === 'idle' || !game.order) {
    const canStart = isHost;
    return shell(
      <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center p-4">
        <Palette size={44} style={{ color: accentColor }} />
        <div className="font-bold" style={{ fontSize: fs * 1.5 }}>Pictionary</div>
        <div className="text-gray-500 max-w-md" style={{ fontSize: fs * 0.9 }}>
          {isTeams
            ? tg('introTeams', { defaultValue: 'Команди по черзі малюють слово, решта вгадує. За кожне вгадане слово — бал команді.' })
            : tg('introDuo', { defaultValue: 'Малюємо по черзі: один малює загадане слово, інший вгадує вголос.' })}
        </div>
        {canStart ? (
          <button type="button" onClick={startGame} className={primaryBtn} style={{ ...btnStyle, fontSize: fs, padding: '7px 20px' }}>
            <Play size={16} /> {tg('start', { defaultValue: 'Почати гру' })}
          </button>
        ) : (
          <div className="text-gray-400" style={{ fontSize: fs * 0.85 }}>{tg('waitingForTeacher', { defaultValue: 'Чекаємо, поки вчитель почне гру…' })}</div>
        )}
      </div>,
    );
  }

  if (game.phase === 'final') {
    const best = Math.max(...scoreboard.map((s) => s.score));
    const leaders = scoreboard.filter((s) => s.score === best && best > 0);
    return shell(
      <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center p-4">
        <Trophy size={44} style={{ color: leaders[0]?.color || accentColor }} />
        <div className="font-bold" style={{ fontSize: fs * 1.4 }}>
          {isTeams && leaders.length === 1
            ? tg('teamWins', { name: leaders[0].name, defaultValue: `Перемагає ${leaders[0].name}!` })
            : tg('gameOver', { defaultValue: 'Гру завершено!' })}
        </div>
        <div className="flex flex-col gap-1.5 min-w-[40%]">
          {[...scoreboard].sort((a, b) => b.score - a.score).map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-6 rounded-xl" style={{ padding: '6px 14px', backgroundColor: 'rgba(0,0,0,0.04)', fontSize: fs }}>
              <span className="inline-flex items-center gap-2 font-medium"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />{s.name}</span>
              <span className="font-bold tabular-nums">{s.score}</span>
            </div>
          ))}
        </div>
        {isHost && (
          <button type="button" onClick={startGame} className={primaryBtn} style={btnStyle}>
            <Play size={15} /> {tg('playAgain', { defaultValue: 'Грати ще' })}
          </button>
        )}
      </div>,
    );
  }

  // drawing / review
  const drawingNow = game.phase === 'drawing';
  const highlightId = isTeams ? currentTeam.id : game.drawer;
  const outOfWords = !game.order || (game.index || 0) + 1 >= game.order.length;
  const nextDrawer = otherRole(game.drawer);
  const nextTeam = teamList[((game.teamIdx || 0) + 1) % teamList.length];
  const canAdvance = isTeams ? isHost : (previewMode || nextDrawer === userRole || outOfWords);

  let headline;
  if (drawingNow) {
    if (seesWord || peeking) {
      headline = (
        <span>
          {tg('draw', { defaultValue: 'Малюй:' })}{' '}
          <span className="font-bold uppercase tracking-wide" style={{ color: accentColor }}>{word}</span>
        </span>
      );
    } else if (isTeams) {
      headline = <span>{tg('teamDraws', { name: currentTeam.name, defaultValue: `Малює ${currentTeam.name}` })}</span>;
    } else {
      headline = <span>{tg('guess', { name: roleName(game.drawer), defaultValue: `Вгадай, що малює ${roleName(game.drawer)}!` })}</span>;
    }
  } else {
    const resultText = game.lastResult === 'guessed'
      ? tg('resultGuessed', { defaultValue: 'Вгадали!' })
      : game.lastResult === 'skipped'
        ? tg('resultSkipped', { defaultValue: 'Пропущено' })
        : tg('resultTimeout', { defaultValue: 'Час вийшов' });
    headline = (
      <span>
        {resultText} {tg('wordWas', { defaultValue: 'Слово:' })}{' '}
        <span className="font-bold uppercase tracking-wide" style={{ color: accentColor }}>{word}</span>
      </span>
    );
  }

  return shell(
    <>
      <div className="shrink-0 flex items-center justify-between gap-3 border-b border-black/5" style={{ padding: '6px 10px' }}>
        <div className="min-w-0 flex items-center gap-2" style={{ fontSize: fs }}>
          {headline}
          {drawingNow && isTeams && !seesWord && (
            <button
              type="button"
              onPointerDown={() => setPeeking(true)}
              onPointerUp={() => setPeeking(false)}
              onPointerLeave={() => setPeeking(false)}
              className={ghostBtn}
              style={{ fontSize: fs * 0.75, padding: '3px 10px', touchAction: 'none' }}
            >
              <Eye size={13} /> {tg('peek', { defaultValue: 'Затисни, щоб побачити слово' })}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {renderScores(highlightId)}
          {drawingNow && (
            <span
              className="inline-flex items-center gap-1 rounded-full font-semibold tabular-nums text-white"
              style={{ fontSize: fs * 0.85, padding: '3px 10px', backgroundColor: timeLeft <= 10 ? '#e11d48' : accentColor }}
            >
              <Timer size={13} /> {timeLeft}
            </span>
          )}
        </div>
      </div>

      <div ref={boxRef} className="relative flex-1 min-h-0" style={{ backgroundColor: canvasColor }}>
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox={`0 0 ${VB_W} ${vbH}`}
          preserveAspectRatio="none"
          style={{ touchAction: canDraw ? 'none' : 'auto', cursor: canDraw ? 'crosshair' : 'default' }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {shownStrokes.map((s) => (
            <path key={s.id} d={pathFor(s.p)} fill="none" stroke={s.c} strokeWidth={s.w} strokeLinecap="round" strokeLinejoin="round" />
          ))}
        </svg>

        {game.phase === 'review' && (
          <div className="absolute inset-x-0 bottom-3 flex justify-center gap-2 pointer-events-none">
            <div className="pointer-events-auto flex items-center gap-2 bg-white/95 rounded-full shadow-lg" style={{ padding: 6 }}>
              {canAdvance ? (
                <button type="button" onClick={outOfWords ? finishGame : nextRound} className={primaryBtn} style={btnStyle}>
                  {outOfWords
                    ? <><Flag size={15} /> {tg('results', { defaultValue: 'Результати' })}</>
                    : <><Play size={15} /> {isTeams
                      ? tg('nextTeam', { name: nextTeam.name, defaultValue: `Далі: ${nextTeam.name}` })
                      : (previewMode
                        ? tg('nextRound', { defaultValue: 'Наступний раунд' })
                        : tg('myTurn', { defaultValue: 'Моя черга малювати' }))}</>}
                </button>
              ) : (
                <span className="text-gray-500 px-3" style={{ fontSize: fs * 0.85 }}>
                  {tg('waitingFor', { name: roleName(nextDrawer), defaultValue: `${roleName(nextDrawer)} малює наступним…` })}
                </span>
              )}
              {isHost && !outOfWords && (
                <button type="button" onClick={finishGame} className={ghostBtn} style={btnStyle}>
                  <Flag size={15} /> {tg('finish', { defaultValue: 'Завершити гру' })}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {drawingNow && (isDrawer || controlsRound) && (
        <div className="shrink-0 flex items-center justify-between gap-2 border-t border-black/5 flex-wrap" style={{ padding: '6px 10px' }}>
          {isDrawer ? (
            <div className="flex items-center gap-1.5 flex-wrap">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => { setColor(c); setErasing(false); }}
                  className="w-6 h-6 rounded-full transition"
                  style={{ backgroundColor: c, boxShadow: !erasing && color === c ? `0 0 0 2px #fff, 0 0 0 4px ${c}` : 'none' }}
                />
              ))}
              <span className="w-px h-5 bg-gray-200 mx-1" />
              {SIZES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => { setSize(s); setErasing(false); }}
                  className={`w-7 h-7 rounded-full flex items-center justify-center ${!erasing && size === s ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
                >
                  <span className="rounded-full bg-gray-700" style={{ width: Math.max(4, s / 2), height: Math.max(4, s / 2) }} />
                </button>
              ))}
              <button type="button" onClick={() => setErasing((v) => !v)} className={`p-1.5 rounded-full ${erasing ? 'bg-gray-200' : 'hover:bg-gray-100'} text-gray-700`} title={tg('eraser', { defaultValue: 'Гумка' })}>
                <Eraser size={16} />
              </button>
              <button type="button" onClick={undo} className="p-1.5 rounded-full hover:bg-gray-100 text-gray-700" title={tg('undo', { defaultValue: 'Скасувати' })}>
                <Undo2 size={16} />
              </button>
              <button type="button" onClick={clear} className="p-1.5 rounded-full hover:bg-gray-100 text-gray-700" title={tg('clear', { defaultValue: 'Очистити' })}>
                <Trash2 size={16} />
              </button>
            </div>
          ) : <span />}
          {controlsRound && (
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={() => endRound('skipped')} className={ghostBtn} style={btnStyle}>
                <SkipForward size={15} /> {tg('skip', { defaultValue: 'Пропустити' })}
              </button>
              <button type="button" onClick={() => endRound('guessed')} className={primaryBtn} style={btnStyle}>
                <Check size={15} /> {tg('guessed', { defaultValue: 'Вгадано' })}
              </button>
            </div>
          )}
        </div>
      )}
    </>,
  );
};

const PictionaryElement = ({ slideScale = 1, ...props }) => (
  <ReferenceScale scale={slideScale}>
    <PictionaryInner {...props} />
  </ReferenceScale>
);

export default PictionaryElement;
